import {
  IonButton,
  IonCheckbox,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useState } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { Icon } from '@/components/memo';
import { useAddDentist } from '@/contexts';
import { DentistHour } from '@/data/DentistHours';
import { useToast } from '@/hooks';
import { DAY_NAMES_ARRAY } from '@/utils/constants';
import { parseTime } from '@/utils/helpers/parseTime';
import { format, isAfter } from 'date-fns';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './Hours.css';

interface HoursProps {}

const Hours: React.FC<HoursProps> = () => {
  const { onAddDentist } = useAddDentist();
  const displayToast = useToast();

  const copy = {
    subHeader: 'WORKING HOURS',
    header: (
      <>
        Set your <span>working</span> hours
      </>
    ),
    message:
      'Please check all the days you are working and specify the working hours for each day.'
  };

  const initialHours = Array.from({ length: 7 }, (_, day) => ({
    day,
    start_time: null,
    end_time: null,
    open: false
  }));

  const [hours, setHours] = useState<DentistHour[]>(initialHours);

  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hours = String(Math.floor((i + 8) / 2) % 24).padStart(2, '0');
    const minutes = (i + 12) % 2 === 0 ? '00' : '30';
    return `${hours}:${minutes}`;
  });

  const onTimeChange = (
    day: number,
    type: 'start_time' | 'end_time',
    value: string
  ) => {
    setHours((prevHours) =>
      prevHours.map((hour) =>
        hour.day === day || !hour.open ? { ...hour, [type]: value } : hour
      )
    );
  };

  const onCheckboxChange = (day: number, open: boolean) => {
    setHours((prevHours) =>
      prevHours.map((hour) => (hour.day === day ? { ...hour, open } : hour))
    );
  };

  const onNext = useCallback(async () => {
    const validHours = hours.filter(
      (hour) =>
        !hour.open ||
        (hour.start_time &&
          hour.end_time &&
          isAfter(parseTime(hour.end_time), parseTime(hour.start_time)))
    );
    if (validHours.length > 0) {
      await onAddDentist(validHours);
    } else {
      displayToast({
        message: 'Please add at least one valid working day and specify hours.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  }, [hours, onAddDentist]);

  return (
    <div className="hours">
      <Spacer top={32} bottom={24}>
        <h4 className="hours__sub-header">{copy.subHeader}</h4>
        <h1 className="hours__header">{copy.header}</h1>
        <p className="hours__message">{copy.message}</p>
      </Spacer>

      <Spacer bottom={124}>
        <div className="hours__working-days">
          {hours.map((hour) => (
            <div key={hour.day} className="hours__working-day">
              <IonCheckbox
                className="hours__day"
                checked={hour.open}
                onIonChange={(e: CustomEvent) =>
                  onCheckboxChange(hour.day, e.detail.checked)
                }
              >
                <p className="hours__day-label">{DAY_NAMES_ARRAY[hour.day]}</p>
              </IonCheckbox>
              {hour.open && (
                <>
                  <Spacer top={12} />
                  <div className="hours__working-hours">
                    <div className="hours__input">
                      <IonSelect
                        label="Start Time"
                        labelPlacement="stacked"
                        value={hour.start_time}
                        placeholder="Select a time"
                        onIonChange={(e: CustomEvent) =>
                          onTimeChange(hour.day, 'start_time', e.detail.value)
                        }
                      >
                        {timeOptions.map((time) => (
                          <IonSelectOption key={time} value={time}>
                            {format(parseTime(time), 'h:mm a')}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </div>
                    <div className="hours__input">
                      <IonSelect
                        label="End Time"
                        labelPlacement="stacked"
                        value={hour.end_time}
                        placeholder="Select a time"
                        onIonChange={(e: CustomEvent) =>
                          onTimeChange(hour.day, 'end_time', e.detail.value)
                        }
                      >
                        {timeOptions
                          .filter(
                            (time) =>
                              !hour.start_time ||
                              isAfter(
                                parseTime(time),
                                parseTime(hour.start_time)
                              )
                          )
                          .map((time) => (
                            <IonSelectOption key={time} value={time}>
                              {format(parseTime(time), 'h:mm a')}
                            </IonSelectOption>
                          ))}
                      </IonSelect>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </Spacer>

      <ButtonContainer>
        <IonButton
          id="next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <Icon slot="start" icon={arrowForwardCircleOutline} />
          Next
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default Hours;
